type DisposableBootstrapComponent = { dispose: () => void };

// Flush a pending transition before disposing so Bootstrap's queued completion callback runs while _element is still valid.
export function disposeBootstrapComponent(
  element: HTMLElement | null | undefined,
  instance: DisposableBootstrapComponent | null | undefined
): void {
  element?.dispatchEvent(new Event("transitionend"));
  // Flushing the element runs Bootstrap's _hideModal, which in turn schedules
  // _resetAdjustments on the backdrop element. Flush the backdrop too, or its
  // orphaned transitionend/fallback timer fires _resetAdjustments after
  // dispose() has nulled _element and crashes (FOBIZZ-TOOLS-50R).
  const backdropElement = (
    instance as
      | { _backdrop?: { _element?: HTMLElement | null } }
      | null
      | undefined
  )?._backdrop?._element;
  backdropElement?.dispatchEvent(new Event("transitionend"));
  // The modal show() path queues its completion callback on _dialog, not the
  // root _element (bootstrap modal.js). executeAfterTransition ignores a
  // transitionend whose target !== its transitionElement, so the root flush
  // above misses _dialog: a modal torn down mid-open leaks that completion,
  // which then dereferences a nulled _focustrap/_element (FOBIZZ-TOOLS-513, 50V).
  // Flush _dialog too. Offcanvas/Toast have no _dialog, so this is a no-op there.
  //
  // _dialog is flushed AFTER the backdrop on purpose: with a visible backdrop,
  // show() animates the backdrop first and only its completion runs
  // _showElement — which queues the modal's own completion on _dialog. A
  // dialog-before-backdrop order therefore missed a modal torn down during
  // the backdrop phase: the backdrop flush spawned a fresh _dialog callback
  // after _dialog had already been flushed, dispose() nulled _config, and the
  // fallback timer crashed with "Cannot read properties of null (reading
  // 'focus')" — surfacing as a stray window error in whatever ran next.
  const dialogElement = (
    instance as { _dialog?: HTMLElement | null } | null | undefined
  )?._dialog;
  dialogElement?.dispatchEvent(new Event("transitionend"));
  // We cannot clearTimeout executeAfterTransition's fallback timer (its id is
  // not exposed). Instead, dispatching transitionend on the *exact* transition
  // element (root, _dialog, backdrop) sets its `called=true`, turning the
  // fallback timer into a no-op. Flushing every in-flight element is the fix.
  try {
    instance?.dispose();
  } catch {
    // element already detached
  }
}

/**
 * Release the document-wide state Bootstrap keeps while a dialog is open —
 * `modal-open`, the inline scroll lock, the `data-bs-*` attributes it parks the
 * original values in, and the backdrops — unless another dialog is still open.
 *
 * Stacked dialogs are normal here — a confirm over a modal, a modal over an
 * off-canvas — and one instance going away must not clear state the one still
 * on screen relies on. The trade-off: an instance torn down while still open
 * alongside another dialog leaves its own backdrop behind. That is the safer of
 * the two failure modes — a stray overlay is visible and clears with the next
 * dialog, whereas releasing a scroll lock another dialog still needs silently
 * breaks the page behind it.
 *
 * The saved `data-bs-overflow` matters as much as the class: a modal opened over
 * an off-canvas parks the off-canvas' `overflow: hidden` there as the "original"
 * value, and the next modal to close restores it — locking a page that has
 * nothing open (#12815).
 *
 * An off-canvas carries `showing` while it opens, with the lock already set, so
 * that state counts as open too. A modal in its backdrop-fade phase has no class
 * yet; that ~150 ms window is not covered: a dialog unmounting then releases
 * the lock and removes the opening modal's backdrop with it. Backdrops cannot
 * stand in as the marker either: BaseModal's force-close fallback fires exactly
 * when Bootstrap left a backdrop behind that has to go.
 */
export function releaseBodyDialogLock(): void {
  if (
    document.querySelector(".modal.show, .offcanvas.show, .offcanvas.showing")
  ) {
    return;
  }

  document
    .querySelectorAll(".modal-backdrop, .offcanvas-backdrop")
    .forEach((backdrop) => backdrop.remove());

  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
  document.body.removeAttribute("data-bs-overflow");
  document.body.removeAttribute("data-bs-padding-right");
}
